import { DataTableFilter, DataTableOrderBy } from "./data-grid-utils";

export const FINISHED_EVENT_PAYLOAD = 'finished';
export const CUSTOM_THEMES_FOLDER = '_custom';

export function isFinishedEvent(payload: string): boolean {
    return (payload ?? '') === FINISHED_EVENT_PAYLOAD;
}

export interface SearchQueryParameters {
    folderId?: number | null;
    pager: Pager;
    orderBy: DataTableOrderBy;
    filter: DataTableFilter;
}

export function serializeSearchQueryParameters(q: SearchQueryParameters): string {
    return JSON.stringify(q);
}

export interface MessageDto {
    message: string;
}

export interface Pager {
    page: number;
    pageSize: number;
    totalRows: number;
    totalPages: number;
}

export function createPager(page: number, pageSize: number, totalRows: number): Pager {
    return {
        page,
        pageSize,
        totalRows,
        totalPages: Math.ceil(totalRows / pageSize),
    };
}

export function areRequestedPagersEqual(pager1: Pager, pager2: Pager): boolean {
    return pager1.page === pager2.page && pager1.pageSize === pager2.pageSize;
}

export interface PostLoginDto {
    event: string;
    firstName: string;
    lastName: string;
    picture: string;
    email: string;
}

export interface PostLoginResponseDto {
    message: string;
    user: UserDto;
}

export interface UserDto {
    preferences?: import('./user-preferences').UserPreferences | null;
    sub: string;
    first_name: string;
    last_name: string;
    picture: string;
    email: string;
    is_admin: number | boolean;
    last_logged_in: string;
    locked: number | boolean;
}

export interface UserGroupDto { id: number; name: string; user_count: number; }
export interface UserGroupsDto { data: UserGroupDto[]; counts: { all: number; ungrouped: number }; }

export interface GetUsersApiResponseDto {
    data: UserDto[];
    pager: Pager;
}

export interface ModuleFolderDto {
    id: number;
    parent_id: number | null;
    name: string;
}

export interface ModuleDto {
    /** Optional learner-specific reading position; absent in author previews. */
    resume_fragment_key?: string | null;
    folder_id?: number | null;
    estimated_minutes?: number | null;
    module_key: string;
    name: string;
    description: string;
    created_at: string; 
    updated_at: string;
    content: string | null | undefined;
    instructions: string | null | undefined;
    prerequisites?: ModuleDependencyDto[];
    is_managed?: boolean;
    theme?: ThemeDto[];
    fragments?: ManagedFragmentDto[]; // ordered list of fragments for a managed module
    finalQuizFragmentKey?: string | null;
}

export interface GetModulesApiResponseDto {
    data: ModuleDto[];
    pager: Pager;
}

export type LearnerFieldAnswer = string | boolean | string[] | Record<string, string>;
export type LearnerModuleAnswers = Record<string, Record<string, LearnerFieldAnswer>>;
export interface GetLearnerModuleAnswersDto {
    answers: LearnerModuleAnswers;
    lockedFragments?: string[];
    readOnly?: boolean;
}

export interface UserModuleDto {
    module_key: string;
    assigned_at: string;
}

export interface GetUserModulesApiResponseDto {
    data: UserModuleDto[];
}

export interface ModuleUserDto {
    sub: string;
    assigned_at: string;
}

export interface GetModuleUsersApiResponseDto {
    data: ModuleUserDto[];
}

export interface SetUserAssignmentsDto {
    modules: string[];
}

export interface SetModuleAssignmentsDto {
    users: string[];
}

export interface MyModuleCourseDto extends ModuleDto {
    key: string,
    name: string
}

export enum ModuleStatusEnum {
    Finished = 2,
    Started = 1,
    NotStarted = 0
}

export function moduleStatusToString(value: ModuleStatusEnum): string {
    return value === ModuleStatusEnum.Finished ? 'Finished' : (value === ModuleStatusEnum.Started ? 'Started' : 'Not Started');
}

export interface MyModuleDto extends ModuleDto {
    is_new: boolean,
    courses: MyModuleCourseDto[] | null,
    status: ModuleStatusEnum,
}

export interface GetMyModulesApiResponseDto {
    data: MyModuleDto[];
    pager: Pager;
}

export interface SessionDto {
    sub: string;
    module_key: string;
    session_id: string;
    started_at: string;
    finished_at: string;
    feedback_courses?: CourseFeedbackPromptDto[];
    completion_email_courses?: CourseFeedbackPromptDto[];
    certificate_names?: PendingCertificateNameDto[];
}

export interface CourseFeedbackPromptDto {
    course_key: string;
    course_name: string;
    certificate_enabled?: boolean;
}

export interface CourseFeedbackInputDto {
    course_key: string;
    stars: number;
    text: string;
    share_with_others: boolean;
}

export interface PublicReviewDto {
    id: number;
    course_key: string;
    course_name: string;
    stars: number;
    text: string;
    created_at: string;
}

export interface PublicReviewsDto {
    data: PublicReviewDto[];
    courses: CourseFeedbackPromptDto[];
    total: number;
    page: number;
    page_size: number;
    average_stars: number | null;
}

export interface FullSessionDto extends SessionDto {
    module: ModuleDto;
}

export interface StartSessionDto {
    module_key: string;
}

export interface FinishSessionDto {
    session_id: string;
}

export interface StatsModuleDto {
    module_key: string;
    module_name: string;
    last_used: string;
}

export interface StatsSessionDto {
    module_key: string;
    module_name: string;
    started_at: string;
    finished_at: string;
}

export interface GetUserStatsApiResponseDto {
    recent_modules: StatsModuleDto[];
    recent_sessions: StatsSessionDto[];
}

export interface CreateSessionEventDto {
    payload: string;
}

export interface SessionEventDto {
    event_id?: string | number;
    session_id: string;
    occured_at: string;
    payload: string;
}

export interface GetUserSessionsApiResponseDto {
    data: SessionDto[];
}

export interface GetUserSessionEventsApiResponseDto {
    data: SessionEventDto[];
}

export type UserQuizFilter = { course_key: string; module_key?: never } | { module_key: string; course_key?: never };

export interface QuizResultEventDto extends SessionEventDto {
    module_key: string;
    module_name: string;
    started_at: string;
    finished_at: string | null;
}

export interface GetUserQuizResultsApiResponseDto {
    data: QuizResultEventDto[];
    saved_answers?: SavedQuizAnswersDto[];
}

export interface SavedQuizAnswerDto {
    field: { key: string; label: string; type: FormFieldType; options?: FormFieldOption[] };
    userAnswer: unknown;
}

export interface SavedQuizAnswersDto {
    module_key: string;
    module_name: string;
    fragment_key: string;
    fragment_title: string;
    updated_at: string;
    answers: SavedQuizAnswerDto[];
}

export interface UserQuizOptionDto {
    course_key: string | null;
    module_key: string;
}

export interface GetUserQuizOptionsApiResponseDto {
    data: UserQuizOptionDto[];
}

export interface SessionSearchDto {
    session_id: string;
    user_name: string;
    email: string;
    module_name: string;
    sub: string;
    started_at: string;
    finished_at: string;
    event_count: number;
}

export interface SearchSessionsApiResponseDto {
    data: SessionSearchDto[];
    pager: Pager;
}

export interface CourseFolderDto {
  id: number;
  parent_id: number | null;
  name: string;
}

export interface ListCourseDto {
  folder_id?: number | null;
  course_key: string;
  name: string;
  description: string | null;
  price: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface ServiceListDto {
    service_key: string;
    name: string;
}

export interface ServiceFormFieldDto {
    key: string;
    label: string;
    type: 'text' | 'email' | 'textarea' | 'radio' | 'messenger-phone';
    required: boolean;
    maxLength?: number;
    helpText?: string;
    options?: { value: string; label: string }[];
    contactMethodField?: string;
    countryCallingCodeSelector?: boolean;
}

export interface ServiceFormDefinitionDto {
    version: 1;
    fields: ServiceFormFieldDto[];
}

export interface ServiceDto extends ServiceListDto {
    content: string;
    form_definition: ServiceFormDefinitionDto;
    created_at: string;
    updated_at: string;
}

export interface ServiceBookingInputDto {
    values: Record<string, string>;
}

export interface GetCoursesApiResponseDto {
  data: ListCourseDto[];
  pager: Pager;   
}

export interface CourseDependencyDto {
  course_key: string;
  is_optional: boolean;
}

export interface ModuleDependencyDto {
  module_key: string;
  is_optional: boolean;
}

export interface CourseModuleDto {
  module_key: string;
  position: number;
  prerequisites: ModuleDependencyDto[];
}

export interface CourseFormComparisonFieldDto {
  earlier_field_key: string;
  later_field_key: string;
}

export interface CourseFormComparisonDto {
  id?: number;
  earlier_fragment_key: string;
  later_fragment_key: string;
  fields: CourseFormComparisonFieldDto[];
}

export interface CourseDto extends ListCourseDto {
  answer_comparisons?: CourseFormComparisonDto[];
  promotions?: CoursePromoDto[];
  loyalty_discounts?: CourseLoyaltyDto[];
  certificate_enabled?: boolean;
  cert_description?: string | null;
  cert_prefix?: string | null;
  modules: CourseModuleDto[];
  course_prerequisites: CourseDependencyDto[];
  users: CourseUserDto[];
}

export interface UserCourseDto {
    course_key: string;
    assigned_at: string;
}

export interface UserCourseRecordDto {
    course_key: string;
    review_id: number | null;
    certificate_key: string | null;
}

export interface GetUserCoursesApiResponseDto {
    data: UserCourseDto[];
}

export interface CourseUserDto {
    sub: string;
    assigned_at: string;
}

export interface GetCourseUsersApiResponseDto {
    data: ModuleUserDto[];
}

export interface SetUserCourseAssignmentsDto {
    courses: string[];
}

export interface SetCourseAssignmentsDto {
    users: string[];
}

export interface PrerequisiteDto {
    key: string; // module_key or course_key
    title: string; // module or course title
    isCourse: boolean; // true if this prerequisite is a course, false if module
}

export interface ParentCourseDto {
    orderIndex?: number;
    key: string; // unique course key
    title: string; // course title
}

export interface LearningItemDto {
    /** A valid saved position beyond the first fragment. */
    can_resume?: boolean;
    estimated_minutes?: number | null;
    key: string; // module_key, the learning path is at the lowes granularity level e.g. listing individual modules
    title: string; // module title
    description?: string | null;
    parentCourses: ParentCourseDto[]; // assigned courses containing this module; empty for standalone assignments
    unmetPrerequisites: PrerequisiteDto[]; // unmet prereqs for the locked learning items
    orderIndex: number;
    is_managed: boolean;
}

export interface LearningPathDto {
    assignedCourses?: ParentCourseDto[];
    available: LearningItemDto[]; // the modules user can start working on right now
    inProgress: LearningItemDto[]; // the modules user has started already, but not finished yet
    locked: LearningItemDto[]; // the modules locked by prerequisites that have to be finished first
    completed: LearningItemDto[]; // the modules locked by prerequisites that have to be finished first
}

export interface LearningInsightsDto {
    days: { date: string; opened: number; completed: number; events: number }[];
    history: { session_id: string; lesson_name: string; started_at: string; completed_at: string | null;
        last_event_at: string | null; event_count: number }[];
    total: number;
    page: number;
    page_size: number;
}

export interface FormFieldAttributesDto {
    container?: Record<string, string>[];
    label?: Record<string, string>[];
    input?: Record<string, string>[];
}

export interface FormFieldOption {
  key: string; 
  value: string; 
}

// ManagedModule domain types
export type FormFieldType =
  | "text"
  | "inline-text"
  | "textarea"
  | "number"
  | "email"
  | "select"
  | "radio"
  | "checkbox"
  | "checkbox-group"
  | "multi-select"
  | "date"
  | 'quiz-list';

export interface FormFieldDto {
  key: string;              // unique field key
  type: FormFieldType;
  label: string;
  is_label_hidden?: boolean;
  required: boolean;
  placeholder?: string;
  maxLength?: number;
  options?: FormFieldOption[];       // for choice fields
  defaultValue?: any;
  expectedValue: string | string[] | null;
  tooltip: string | null,
  aiHint?: string | null;    // author guidance for AI answer verification
  weight?: number | null;   // maximum points; missing or zero means 1
  attributes?: FormFieldAttributesDto;
}

export type ActionType =
  | "compareAnswers"
  | "gotoNext"
  | "gotoPrevious"
  | "gotoFragment"
  | "validateForm"
  | "showForm"
  | "resetForm";

export interface ValidateFormPayloadDto {
  toOnAllCorrect: string;
  toOnIncorrect: string;
}

export interface FragmentActionDto {
  key: string;          // unique action key
  order: number;        // explicit ordering index within the fragment
  label: string;        // button text shown to the end user
  type: ActionType;     // determines behavior
  to: string;           // "next" | "prev" | fragmentKey | "self"
  payload?: any;        // extensible for future action types
}

export interface ManagedFragmentDto {
  key: string;                // unique fragment key
  order: number;              // explicit ordering index (0-based or 1-based)
  title: string;              // title shown above HTML
  instructions: string;
  answers_explanation?: string | null;
  aiHint?: string | null;    // optional instructions for AI when explaining this fragment
  html: string;               // TipTap HTML content
  hidden?: number | boolean;
  fillOnce?: number | boolean;
  actions: FragmentActionDto[];  // ordered list of actions belonging to this fragment
  formFields: FormFieldDto[];
}

export function getActionText(action: FragmentActionDto, fragments: ManagedFragmentDto[]) {
  switch (action.type) {
    case "gotoNext":
      return `Label: ${action.label} — go to next fragment`;

    case "gotoPrevious":
      return `Label: ${action.label} — go to previous fragment`;

    case "gotoFragment":
      const target = fragments.find(f => f.key === action.to);
      return `Label: ${action.label} — go to fragment "${target?.title ?? "Unknown"}"`;

    case "validateForm":
      return `Label: ${action.label} — validate user input`;

    case "showForm":
      return `Label: ${action.label} — show answers`;

    case "resetForm":
      return `Label: ${action.label} — try again (reset form)`;

    case "compareAnswers":
      return `Label: ${action.label} — compare answers with an earlier course form`;

  }
  return 'Unknown';
}

export interface UploadFileDto {
    name: string;
    data: File;
}







export type AiAction = 'help' | 'translate' | 'validate_form' | 'explain_selection';

export type AiValidationVerdict = 'correct' | 'partial' | 'incorrect';

export interface AiFormFieldValidationDto {
  fieldKey: string;
  label: string;
  type: string;
  userAnswer: string;
  expectedAnswer?: string;
  tooltip?: string;
  aiHint?: string;
  weight?: number;
  selectedOptionKeys?: string[];
  expectedOptionKeys?: string[];
}

export interface AiValidationFieldResultDto {
  fieldKey: string;
  verdict: AiValidationVerdict;
  score?: number;
  feedback: string;
}

export interface AiValidationSummaryDto {
  overallImpression: string;
  strengths: string[];
  priorities: string[];
}

export interface AiValidationResponseDataDto {
  fieldResults: AiValidationFieldResultDto[];
  summary: AiValidationSummaryDto;
}

export interface AiSelectionTableContextDto {
  cellText?: string;
  rowHeader?: string;
  columnHeader?: string;
}

export interface AiSelectionContextDto {
  selectedText: string;
  containingSentence?: string;
  containingBlockText?: string;
  containingBlockHtml?: string;
  nearestElementTag?: string;
  domPath?: string;
  tableContext?: AiSelectionTableContextDto;
}

export interface AiRequestDto {
  action: AiAction;
  html: string;
  fragmentTitle?: string;
  authorHint?: string | null;
  preferredLanguage?: string;
  targetLanguage?: string;
  moduleKey?: string;
  fragmentKey?: string;
  mode?: 'concise' | 'detailed';
  includeImages?: boolean;
  formFields?: AiFormFieldValidationDto[];
  selectionContext?: AiSelectionContextDto;
}

export interface AiTextResponseDto {
  data: string;
  prompt: any;
}

export interface AiValidationResponseDto {
  data: AiValidationResponseDataDto;
  prompt: any;
}

export type AiResponseDto = AiTextResponseDto | AiValidationResponseDto;

export interface ThemeDto {
  label: string;
  value: string;
}

export interface GetThemesDto {
  standard: ThemeDto[];
  custom: ThemeDto[];
}

export interface CssProperty {
    property: string;
    value: string;
}

export interface ThemeNode {
    selector: string;
    properties: CssProperty[];
    children: ThemeNode[];
}

export interface ThemeCategory {
    title: string;
    children: ThemeNode[];
}

export function isMessageDto(value: unknown): value is MessageDto {
  return !!value && typeof value === 'object' && 'message' in value;
}

export function isAiValidationResponseDto(value: unknown): value is AiValidationResponseDto {
  if (!value || typeof value !== 'object' || !('data' in value)) {
    return false;
  }

  const data = (value as any).data;

  return !!data
    && typeof data === 'object'
    && Array.isArray(data.fieldResults)
    && !!data.summary
    && typeof data.summary === 'object'
    && typeof data.summary.overallImpression === 'string'
    && Array.isArray(data.summary.strengths)
    && Array.isArray(data.summary.priorities);
}

export function isAiTextResponseDto(value: unknown): value is AiTextResponseDto {
  return !!value
    && typeof value === 'object'
    && 'data' in value
    && typeof (value as any).data === 'string';
}
export interface PurchaseQuoteDto {
    loyalty_discount_percent?: string | null;
    promo_discount_percent?: string | null;
    loyalty_course_key?: string | null;
    loyalty_course_name?: string | null;
    course_key: string;
    course_name: string;
    quoted_amount_minor: number;
    payable_amount_minor: number;
    discount_amount_minor: number;
    promo_code: string | null;
    discount_percent: string | null;
    currency_code: string;
}

export interface PurchaseDto extends PurchaseQuoteDto {
    checkout_created?: boolean;
    purchase_key: string;
    paddle_transaction_id: string | null;
    status: 'creating' | 'creation_uncertain' | 'creation_failed' | 'pending' | 'canceling' | 'confirming' | 'completed' | 'canceled';
    fulfilled_at: string | null;
    created_at: string;
}

export interface PaymentConfigDto {
    environment: 'sandbox' | 'production';
    client_token: string;
}

export interface CoursePromoDto {
    draft_key?: string;
    id?: number;
    code: string;
    discount_percent: string;
    starts_on: string;
    ends_on: string;
    enabled: boolean;
    validity_mode: 'dates' | 'purchases';
    purchase_limit: number | null;
    completed_purchases?: number;
    reserved_purchases?: number;
    remaining_purchases?: number | null;
}

export interface CourseLoyaltyDto {
    draft_key?: string;
    id?: number;
    target_course_key: string;
    target_course_name?: string;
    discount_percent: string;
}

export interface ProtectedStorageEntryDto {
    name: string;
    path: string;
    type: 'file' | 'folder';
    size: number | null;
    modified_at: string;
    download_url?: string;
}

export interface ProtectedStorageListingDto {
    path: string;
    entries: ProtectedStorageEntryDto[];
}

export interface ProtectedStorageDeleteDto {
    deleted: boolean;
    path: string;
}

export interface AdminReviewDto extends PublicReviewDto {
    reviewer_name: string;
    reviewer_email: string | null;
    share_with_others: boolean;
    approved_by_admin: boolean;
}

export interface ReviewFiltersDto {
    course: string | null;
    shared: string;
    approved: string;
    stars: string;
}

export interface AdminReviewsDto {
    data: AdminReviewDto[];
    courses: CourseFeedbackPromptDto[];
    total: number;
    page: number;
    page_size: number;
}

export interface ModuleUseAgreementDto {
    accepted: boolean;
    accepted_at: string | null;
    notice_version: string;
}
export interface LearnerCertificateDto {
    key: string;
    course_name: string;
    issued_at: string;
    download_name?: string;
    name_confirmed?: boolean;
    default_name?: string;
}

export interface PendingCertificateNameDto extends CourseFeedbackPromptDto {
    key: string;
    default_name: string;
    offer_review: boolean;
}

export interface AdminCertificateDto extends LearnerCertificateDto {
    id: number;
    user_id: number;
    course_id: number;
    recipient_name: string | null;
    revoked_at: string | null;
}

export interface AdminCertificatesDto {
    data: AdminCertificateDto[];
    page: number;
    page_size: number;
    total: number;
}
